import type { StaffPerformanceData } from "@/actions/ssr/staff-performance/getStaffPerformanceSSR";
import type { RewardsCard } from "@/components/performance/portal/rewards/rewardTypes";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

const FALLBACK: StaffPerformanceData = {
  kpis: {
    current_rank: { value: "—" },
    total_points: { value: "—" },
    completed_tasks: { value: "—" },
    rejected_tasks: { value: "—" },
  },
  chart: [],
  leaderboard: [],
  currentStaffId: null,
};

interface StaffPerformanceState {
  data: StaffPerformanceData;
  clientFetched: boolean;
  /** Page-level date range the user picked. Wired to chart + leaderboard. */
  dateFilter: { from: string | null; to: string | null };
  /** §14.1 rewards card (tier + progress + all badges). null until seeded. */
  rewards: RewardsCard | null;
}

const initialState: StaffPerformanceState = {
  data: FALLBACK,
  clientFetched: false,
  dateFilter: { from: null, to: null },
  rewards: null,
};

const staffPerformanceSlice = createSlice({
  name: "staffPerformance",
  initialState,
  reducers: {
    setStaffPerformanceFromSSR(
      state,
      action: PayloadAction<StaffPerformanceData>,
    ) {
      // SSR seeds the store only until a client fetch takes over.
      if (state.clientFetched) return;
      state.data = action.payload;
    },
    setStaffPerformance(state, action: PayloadAction<StaffPerformanceData>) {
      state.clientFetched = true;
      state.data = action.payload;
    },
    /**
     * Partial update for the leaderboard rows only — used by
     * `<StaffLeaderboardCard>`'s client-side re-fetch so the rest of the
     * staff-portal Performance data (KPIs, chart, currentStaffId) stays put.
     */
    setStaffPerformanceLeaderboard(
      state,
      action: PayloadAction<StaffPerformanceData["leaderboard"]>,
    ) {
      state.data.leaderboard = action.payload;
    },
    /**
     * Partial update for the chart series only — used by
     * `<StaffTopPerformerCard>`'s client-side re-fetch so the rest of
     * the staff-portal Performance data (KPIs, leaderboard,
     * currentStaffId) stays put. Spreading the render-closure `data`
     * into a full `setStaffPerformance` payload would clobber the KPIs
     * back to the FALLBACK (`—`) whenever the closure captures
     * pre-SSR-seed state — this partial action sidesteps that.
     */
    setStaffPerformanceChart(
      state,
      action: PayloadAction<{ label: string; value: number }[]>,
    ) {
      state.data.chart = action.payload;
    },
    /**
     * Partial update for the four stat cards only — used by
     * `<StaffPerformanceStatCards>`'s client-side re-fetch of the §24 wallet
     * when the page date filter changes. Same reasoning as the chart and
     * leaderboard partials: a full `setStaffPerformance` would clobber the
     * other sections back to FALLBACK.
     */
    setStaffPerformanceKpis(
      state,
      action: PayloadAction<StaffPerformanceData["kpis"]>,
    ) {
      state.data.kpis = action.payload;
    },
    /**
     * Page-level date filter. Dispatched by the page header's
     * `<CDatePickerWithPresets>` and consumed by `<StaffTopPerformerCard>`
     * (chart) and `<StaffLeaderboardCard>` (leaderboard) via
     * `useFilteredFetch` `globalFilterSelector`.
     *
     * Both BE endpoints now honour `date_from` / `date_to` (verified v0.73,
     * 2026-06-09 — see `staff-v2/targets.md` §23 and
     * `fe-skill/backend-requirement-2026-06-09-leaderboard-date-filter.md`).
     * Chart cap = 90 days, leaderboard cap = 365 days. Over-cap / bad
     * format / `date_from > date_to` → 400 `invalid_date_range` with the
     * same envelope shape across all three endpoints.
     */
    setStaffPerformanceDateFilter(
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>,
    ) {
      state.dateFilter = action.payload;
    },
    /** Seed the §14.1 rewards card from SSR. */
    setStaffRewardsFromSSR(state, action: PayloadAction<RewardsCard>) {
      state.rewards = action.payload;
    },
    /** Client-side overwrite — period switcher on the TierProgressCard. */
    setStaffRewards(state, action: PayloadAction<RewardsCard>) {
      state.rewards = action.payload;
    },
  },
});

export const {
  setStaffPerformanceFromSSR,
  setStaffPerformance,
  setStaffPerformanceLeaderboard,
  setStaffPerformanceChart,
  setStaffPerformanceKpis,
  setStaffPerformanceDateFilter,
  setStaffRewardsFromSSR,
  setStaffRewards,
} = staffPerformanceSlice.actions;
export default staffPerformanceSlice.reducer;
